// react_frontend/src/Components/Modules/PurchaseSetting/EditPurchase.js
import React, { useState, useEffect, useContext } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { MotherContext } from "../../../Context/MotherContext";

const EditPurchase = () => {
  const { categories, items, startCategoryPolling, startItemPolling } = useContext(MotherContext);
  const [data, setData] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const [quantity, setQuantity] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [price, setPrice] = useState("");

  // Start polling for categories and items
  useEffect(() => {
    startCategoryPolling();
    startItemPolling();
  }, [startCategoryPolling, startItemPolling]);

  // Fetch purchase data
  useEffect(() => {
    AxiosAuthInstance.get(`purchases/${id}`).then((response) => {
      const purchase = response?.data?.purchase;
      setData(purchase);
      setQuantity(purchase?.quantity || "");
      setUnitPrice(purchase?.unit_price || "");
      setPrice(purchase?.price || "");
    });
  }, [id]);

  // Recalculate price
  useEffect(() => {
    const qty = parseFloat(quantity);
    const unit = parseFloat(unitPrice);
    if (!isNaN(qty) && !isNaN(unit)) {
      setPrice((qty * unit).toFixed(2));
    } else {
      setPrice("");
    }
  }, [quantity, unitPrice]);

  // Get active items for a category (from context)
  const getItemsByCategory = (categoryId) => {
    return items.filter(item => item.category_id == categoryId && item.item_status === 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    AxiosAuthInstance.put(`purchases/${id}`, {
      category_id: e.currentTarget.category_id.value,
      purchase_date: e.currentTarget.purchase_date.value,
      receipt_no: e.currentTarget.receipt_no.value,
      organization_name: e.currentTarget.organization_name.value,
      organization_address: e.currentTarget.organization_address.value,
      item_id: e.currentTarget.item_id.value,
      quantity: quantity,
      product_unit: e.currentTarget.product_unit.value,
      unit_price: unitPrice,
      price: price,
      note: e.currentTarget.note.value,
    })
      .then((response) => {
        if (response.data.status === true) {
          navigate("/purchase_list");
        }
      })
      .catch((error) => {
        setErrors(error.response?.data?.errors || {});
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (!data) return <p>Loading...</p>;

  return (
    <>
      <Breadcrumb title={"Edit Purchase"} />
      <div className="row">
        <div className="col-md-12">
          <div className="card custom-bg text-white">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h4>Edit Purchase</h4>
                <Link to="/purchase_list" className="text-white text-decoration-none">
                  <button className="btn btn-sm custom-bg text-white">
                    <i className="fa-solid fa-list"></i> Purchase List
                  </button>
                </Link>
              </div>
            </div>
            <div className="card-body bg-white">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-2">
                    <label><p className="text-dark">Category</p></label>
                    <select
                      name="category_id"
                      className="form-select"
                      defaultValue={data.category_id}
                    >
                      <option value="" disabled>Select Category</option>
                      {categories
                        .filter(cat => cat.category_status === 1) // ✅ Only active categories
                        .map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                        ))}
                    </select>
                    <p className="text-danger"><small>{errors.category_id?.[0]}</small></p>
                  </div>

                  <div className="col-md-2 mx-1">
                    <label><p className="text-dark">Purchase date</p></label>
                    <input
                      type="date"
                      name="purchase_date"
                      defaultValue={data.purchase_date}
                      className={errors.purchase_date ? "form-control mb-4 is-invalid" : "form-control mb-4"}
                    />
                    <p className="text-danger"><small>{errors.purchase_date?.[0]}</small></p>
                  </div>

                  <div className="col-md-1 mx-1">
                    <label><p className="text-dark">Receipt no</p></label>
                    <input
                      type="text"
                      name="receipt_no"
                      defaultValue={data.receipt_no}
                      className={errors.receipt_no ? "form-control mb-4 is-invalid" : "form-control mb-4"}
                    />
                    <p className="text-danger"><small>{errors.receipt_no?.[0]}</small></p>
                  </div>

                  <div className="col-md-3 mx-1">
                    <label><p className="text-dark">Organization name</p></label>
                    <input
                      type="text"
                      name="organization_name"
                      defaultValue={data.organization_name}
                      className={errors.organization_name ? "form-control mb-4 is-invalid" : "form-control mb-4"}
                    />
                    <p className="text-danger"><small>{errors.organization_name?.[0]}</small></p>
                  </div>

                  <div className="col-md-3 mx-1">
                    <label><p className="text-dark">Organization address</p></label>
                    <input
                      type="text"
                      name="organization_address"
                      defaultValue={data.organization_address}
                      className={errors.organization_address ? "form-control mb-4 is-invalid" : "form-control mb-4"}
                    />
                    <p className="text-danger"><small>{errors.organization_address?.[0]}</small></p>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4 mx-1">
                    <label><p className="text-dark">Item</p></label>
                    <select
                      name="item_id"
                      className="form-select"
                      defaultValue={data.item_id}
                    >
                      <option value="" disabled>Select Item</option>
                      {data.category_id && getItemsByCategory(data.category_id).map((item) => (
                        <option key={item.id} value={item.id}>{item.item_name}</option>
                      ))}
                    </select>
                    <p className="text-danger"><small>{errors.item_id?.[0]}</small></p>
                  </div>

                  <div className="col-md-1 mx-1">
                    <label><p className="text-dark">Quantity</p></label>
                    <input
                      type="number"
                      name="quantity"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className={errors.quantity ? "form-control mb-4 is-invalid" : "form-control mb-4"}
                      placeholder="Quantity"
                      min="0"
                      step="any"
                    />
                    <p className="text-danger"><small>{errors.quantity?.[0]}</small></p>
                  </div>

                  <div className="col-md-1 mx-1">
                    <label><p className="text-dark">Product Unit</p></label>
                    <input
                      type="text"
                      name="product_unit"
                      value={data.product_unit}
                      readOnly
                      className="form-control mb-4"
                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                    <p className="text-danger"><small>{errors.product_unit?.[0]}</small></p>
                  </div>

                  <div className="col-md-1 mx-1">
                    <label><p className="text-dark">Unit price</p></label>
                    <input
                      type="number"
                      name="unit_price"
                      value={unitPrice}
                      onChange={(e) => setUnitPrice(e.target.value)}
                      className={errors.unit_price ? "form-control mb-4 is-invalid" : "form-control mb-4"}
                      placeholder="Unit price"
                      min="0"
                      step="any"
                    />
                    <p className="text-danger"><small>{errors.unit_price?.[0]}</small></p>
                  </div>

                  <div className="col-md-1 mx-1">
                    <label><p className="text-dark">Price</p></label>
                    <input
                      type="number"
                      name="price"
                      value={price}
                      readOnly
                      className={errors.price ? "form-control mb-4 is-invalid" : "form-control mb-4"}
                      placeholder="Price"
                    />
                    <p className="text-danger"><small>{errors.price?.[0]}</small></p>
                  </div>

                  <div className="col-md-2 mx-1">
                    <label><p className="text-dark">Note</p></label>
                    <input
                      type="text"
                      name="note"
                      defaultValue={data.note}
                      className={errors.note ? "form-control mb-4 is-invalid" : "form-control mb-4"}
                      placeholder="Enter Note if any?"
                    />
                    <p className="text-danger"><small>{errors.note?.[0]}</small></p>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12 my-4">
                    <button
                      type="submit"
                      className="btn custom-bg text-white btn-md"
                      disabled={isLoading}
                      dangerouslySetInnerHTML={{
                        __html: isLoading
                          ? '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Loading...'
                          : "Edit Purchase",
                      }}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditPurchase;